import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BarChart, Bar, XAxis, YAxis, LineChart, Line, Tooltip, ResponsiveContainer, Legend } from "recharts";
import "leaflet/dist/leaflet.css";
import Header from "../components/layout/Header";
import { Store, Users, Cpu, CreditCard, AlertCircle, AlertTriangle, CheckCircle, FolderKanban } from "lucide-react";
import MapSection from "../components/map/MapSection";
import { dashboardAPI, cascadeAPI, projetsAPI } from "../api/services";
import { useSocket } from "../context/SocketContext";
import { useAuth } from "../context/AuthContext";

export default function VueEnsemble() {
  const { t } = useTranslation();
  const { user, adminMode } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isRevendeur = user?.role === 'revendeur';
  const isMembre = user?.role === 'membre';
  const isClient = user?.role === 'client';

  const [stats, setStats] = useState(null);
  const [topRevendeurs, setTopRevendeurs] = useState([]);
  const [dataAbonnements, setDataAbonnements] = useState([]);
  const [dataStatut, setDataStatut] = useState([]);
  const [alertes, setAlertes] = useState([]);
  const [loading, setLoading] = useState(true);
  const { lastAlert, lastStatusChange } = useSocket();

  // Cascade filters (admin + revendeur only)
  const [revendeurs, setRevendeurs] = useState([]);
  const [clients, setClients] = useState([]);
  const [projets, setProjets] = useState([]);
  const [filterRev, setFilterRev] = useState("");
  const [filterCli, setFilterCli] = useState("");
  const [filterProj, setFilterProj] = useState("");

  // Load cascade options (admin/revendeur)
  useEffect(() => {
    if (isMembre || isClient) return;
    if (isAdmin) {
      cascadeAPI.revendeurs().then(res => setRevendeurs(res.data?.data || res.data || [])).catch(() => {});
    }
  }, [isAdmin, isMembre]);

  useEffect(() => {
    if (isMembre || isClient) return;
    cascadeAPI.clients(filterRev || undefined).then(res => setClients(res.data?.data || res.data || [])).catch(() => {});
  }, [filterRev, isMembre]);

  useEffect(() => {
    if (isMembre || isClient) return;
    cascadeAPI.projets(filterCli || undefined, filterRev || undefined).then(res => setProjets(res.data?.data || res.data || [])).catch(() => {});
  }, [filterCli, filterRev, isMembre]);

  // Membre : charger ses projets IoT
  useEffect(() => {
    if (!isMembre) return;
    projetsAPI.mesProjetsIoT().then(res => {
      setProjets(res.data?.data || res.data || []);
    }).catch(() => {});
  }, [isMembre]);

  const handleRev = (v) => { setFilterRev(v); setFilterCli(""); setFilterProj(""); };
  const handleCli = (v) => { setFilterCli(v); setFilterProj(""); };

  // Rafraîchir les stats quand un événement temps réel arrive
  useEffect(() => {
    if (!lastAlert && !lastStatusChange) return;
    dashboardAPI.stats().then(res => setStats(res.data?.data || res.data)).catch(() => {});
    dashboardAPI.alertesRecentes().then(res => setAlertes(res.data?.data || res.data || [])).catch(() => {});
  }, [lastAlert, lastStatusChange]);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const promises = [
          dashboardAPI.stats(),
          dashboardAPI.chartsStatut(),
          dashboardAPI.alertesRecentes(),
        ];
        // Admin/Revendeur : top revendeurs + abonnements
        if (!isMembre && !isClient) {
          promises.push(dashboardAPI.chartsAbonnements());
        }
        if (isAdmin) {
          promises.push(dashboardAPI.topRevendeurs());
        }

        const results = await Promise.all(promises);
        setStats(results[0].data?.data || results[0].data);
        setDataStatut(results[1].data?.data || results[1].data || []);
        setAlertes(results[2].data?.data || results[2].data || []);
        if (!isMembre && !isClient) {
          setDataAbonnements(results[3].data?.data || results[3].data || []);
        }
        if (isAdmin) {
          const topIdx = isMembre ? 3 : 4;
          setTopRevendeurs(results[topIdx].data?.data || results[topIdx].data || []);
        }
      } catch (err) {
        console.error("Erreur chargement dashboard:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Stat cards adaptées au rôle
  const buildStatCards = () => {
    if (!stats) return [];
    const cards = [];

    // Admin : revendeurs actifs
    if (isAdmin) {
      cards.push({ icon: Store, bg: "bg-blue-100", color: "text-blue-600", label: t('dashboard.activeResellers'), value: stats.revendeurs?.toString() || "0" });
    }

    // Admin + Revendeur : clients
    if (!isMembre && !isClient) {
      cards.push({ icon: Users, bg: "bg-purple-100", color: "text-purple-600", label: t('dashboard.clients'), value: stats.clients?.toString() || "0" });
    }

    // Tous : appareils déployés
    cards.push({ icon: Cpu, bg: "bg-green-100", color: "text-green-600", label: t('dashboard.deployedDevices'), value: stats.appareils?.toLocaleString("fr-FR") || "0", sub: `${stats.appareilsEnLigne || 0} ${t('dashboard.online')}` });

    // Admin + Revendeur : abonnements
    if (!isMembre && !isClient) {
      cards.push({ icon: CreditCard, bg: "bg-orange-100", color: "text-orange-500", label: t('dashboard.activeSubscriptions'), value: stats.abonnements?.toLocaleString("fr-FR") || "0" });
    }

    // Membre : projets
    if (isMembre) {
      cards.push({ icon: FolderKanban, bg: "bg-blue-100", color: "text-blue-600", label: t('dashboard.projects'), value: stats.projets?.toString() || "0" });
    }

    return cards;
  };

  const statCards = buildStatCards();

  const selectStyle = "px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-40";

  return (
    <>
      <Header title={t('dashboard.title')} subtitle="" />

      {/* Filtres — Admin : cascade complète, Revendeur : client/projet, Membre : projet uniquement, Client : aucun filtre */}
      {!isMembre && !isClient && (
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">{t('dashboard.filters')}</h3>
          <div className="flex gap-3 flex-wrap">
            {adminMode === 'platform' && (
              <div className="flex flex-col gap-1">
                <label className="text-xs text-slate-500">{t('dashboard.reseller')}</label>
                <select value={filterRev} onChange={e => handleRev(e.target.value)} className={selectStyle}>
                  <option value="">{t('dashboard.allResellers')}</option>
                  {revendeurs.map(r => <option key={r._id} value={r._id}>{r.nom}</option>)}
                </select>
              </div>
            )}
            <div className="flex flex-col gap-1">
              <label className="text-xs text-slate-500">{t('dashboard.client')}</label>
              <select value={filterCli} onChange={e => handleCli(e.target.value)} className={selectStyle}>
                <option value="">{t('dashboard.allClients')}</option>
                {clients.map(c => <option key={c._id} value={c._id}>{c.nom}</option>)}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-slate-500">{t('dashboard.project')}</label>
              <select value={filterProj} onChange={e => setFilterProj(e.target.value)} className={selectStyle}>
                <option value="">{t('dashboard.allProjects')}</option>
                {projets.map(p => <option key={p._id} value={p._id}>{p.nom}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Membre : filtre projet simple */}
      {isMembre && projets.length > 0 && (
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">{t('dashboard.filters')}</h3>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500">{t('dashboard.project')}</label>
            <select value={filterProj} onChange={e => setFilterProj(e.target.value)} className={selectStyle}>
              <option value="">{t('dashboard.allProjects')}</option>
              {projets.map(p => <option key={p._id} value={p._id}>{p.nom}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* Carte */}
      <div className="bg-slate-900 rounded-2xl overflow-hidden mb-6">
        <MapSection focusProjetId={filterProj} filterRevendeur={filterRev} filterClient={filterCli} onlyTermine={isClient} />
      </div>

      {/* Stats cards */}
      {loading ? (
        <div className={`grid grid-cols-2 md:grid-cols-${statCards.length || 4} gap-3 md:gap-4 mb-6`}>
          {[1,2,3,4].map(i => (
            <div key={i} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 animate-pulse">
              <div className="w-12 h-12 rounded-xl bg-gray-200 mb-3" />
              <div className="h-4 bg-gray-200 rounded w-24 mb-2" />
              <div className="h-8 bg-gray-200 rounded w-16 mb-1" />
              <div className="h-3 bg-gray-200 rounded w-32" />
            </div>
          ))}
        </div>
      ) : (
        <div className={`grid grid-cols-2 md:grid-cols-${statCards.length} gap-3 md:gap-4 mb-6`}>
          {statCards.map((s) => (
            <div key={s.label} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 relative">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${s.bg} mb-3`}>
                <s.icon size={22} className={s.color} />
              </div>
              <p className="text-slate-500 text-sm">{s.label}</p>
              <p className="text-3xl font-bold text-slate-800 my-1">{s.value}</p>
              {s.sub && <p className="text-slate-400 text-xs">{s.sub}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Graphiques */}
      <div className={`grid grid-cols-1 ${!isMembre ? 'lg:grid-cols-2' : ''} gap-4 md:gap-6 mb-6`}>
        {/* Graphique abonnements — Admin + Revendeur */}
        {!isMembre && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <h3 className="font-semibold text-slate-800 mb-0.5">{t('dashboard.subscriptionEvolution')}</h3>
            <p className="text-slate-400 text-xs mb-4">{t('dashboard.last6Months')}</p>
            {loading ? (
              <div className="h-[200px] flex items-center justify-center">
                <div className="inline-block w-8 h-8 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
              </div>
            ) : dataAbonnements.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={dataAbonnements}>
                  <XAxis dataKey="mois" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <Tooltip />
                  <Bar dataKey="valeur" fill="#f97316" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-slate-400 text-sm">{t('dashboard.noData')}</div>
            )}
          </div>
        )}

        {/* Graphique statut appareils — Tous */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <h3 className="font-semibold text-slate-800 mb-0.5">{t('dashboard.deviceStatus')}</h3>
          <p className="text-slate-400 text-xs mb-4">{t('dashboard.last7Days')}</p>
          {loading ? (
            <div className="h-[200px] flex items-center justify-center">
              <div className="inline-block w-8 h-8 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
            </div>
          ) : dataStatut.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={dataStatut}>
                <XAxis dataKey="jour" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <Tooltip />
                <Legend formatter={(v) => v === "actifs" ? t('dashboard.active') : t('dashboard.inactive')} />
                <Line type="monotone" dataKey="actifs" stroke="#22c55e" strokeWidth={2} dot={false} name="actifs" />
                <Line type="monotone" dataKey="inactifs" stroke="#ef4444" strokeWidth={2} dot={false} name="inactifs" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-slate-400 text-sm">{t('dashboard.noData')}</div>
          )}
        </div>
      </div>

      {/* Alertes + Top revendeurs (admin) / Alertes seules (revendeur/membre) */}
      <div className={`grid grid-cols-1 ${isAdmin ? 'lg:grid-cols-2' : ''} gap-4 md:gap-6`}>
        {/* Alertes récentes — Tous */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800">{t('dashboard.recentAlerts')}</h3>
            {alertes.length > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">{alertes.length} {t('dashboard.new')}</span>
            )}
          </div>
          {loading ? (
            <div className="py-8 text-center">
              <div className="inline-block w-8 h-8 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
            </div>
          ) : alertes.length > 0 ? (
            <div className="space-y-4">
              {alertes.map((a) => (
                <div key={a._id || a.id} className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    a.type === "Critique" ? "bg-red-100" : a.type === "Avertissement" ? "bg-yellow-100" : "bg-blue-100"
                  }`}>
                    {a.type === "Critique" ? <AlertCircle size={16} className="text-red-500" /> :
                     a.type === "Avertissement" ? <AlertTriangle size={16} className="text-yellow-500" /> :
                     <CheckCircle size={16} className="text-blue-500" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between">
                      <p className="text-sm font-semibold text-slate-800">{a.label}</p>
                    </div>
                    <p className="text-xs text-slate-500">{a.sn}</p>
                    <p className="text-xs text-slate-400">{a.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-8">{t('dashboard.noAlerts')}</p>
          )}
        </div>

        {/* Top revendeurs — Admin uniquement */}
        {isAdmin && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <h3 className="font-semibold text-slate-800 mb-4">{t('dashboard.topResellers')}</h3>
            {loading ? (
              <div className="py-8 text-center">
                <div className="inline-block w-8 h-8 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
              </div>
            ) : topRevendeurs.length > 0 ? (
              <div className="space-y-5">
                {topRevendeurs.map((r) => (
                  <div key={r.nom || r._id}>
                    <div className="flex justify-between mb-1">
                      <p className="text-sm font-semibold text-slate-800">{r.nom}</p>
                      <p className="text-sm font-semibold text-slate-800">{r.appareils} {t('dashboard.devices')}</p>
                    </div>
                    <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-600 rounded-full" style={{ width: `${r.pct}%` }} />
                    </div>
                    <div className="flex justify-between mt-1">
                      <p className="text-xs text-slate-400">{r.clients} {t('dashboard.clientsLabel')}</p>
                      <p className="text-xs text-slate-400">{r.revenue ? `${r.revenue.toLocaleString("fr-FR")} EUR` : ""}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400 text-center py-8">{t('dashboard.noReseller')}</p>
            )}
          </div>
        )}
      </div>
    </>
  );
}
